import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { InitAppApi } from '@app/app.const';
import { AdminConfig, AppInitConfig } from '@shared/interfaces/appInitConfig';
import { ConsumerLoanGroup } from '@shared/model/lender-config.model';
import { firstValueFrom } from 'rxjs';
import { RuntimeConfigService } from './runtime-config.service';

/**
 * Initialize and set AppConfig data
 */
@Injectable({
  providedIn: 'root'
})
export class InitializeAppConfigDataService {
  public appConfigData: AppInitConfig;
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {}

  /**
   * Initializes the application configuration by fetching it from the AppConfigService.
   *
   * It finds the 'consumerPortalApi' configuration, constructs the URL and headers based on the configuration,
   * and then retrieves the application configuration data from that URL.
   *
   * @returns A promise that resolves when the application configuration has been successfully initialized.
   * If the promise is rejected, it indicates that an error occurred while initializing the application configuration.
   *
   * @throws Will throw an error if the 'consumerPortalApi' configuration is not found in the received data
   * or if there is a problem while fetching the application configuration data.
   */
  public async initAppConfig(): Promise<void> {
    try {
      const config = this.runtimeConfigService.getConfig;
      const endpoint = InitAppApi.url;
      const baseUrl = this.runtimeConfigService.getDXAPIBaseUrl();
      const url = `${baseUrl}/${endpoint}`;
      const headers = config?.consumerPortalApi?.headers;

      await this.getAppConfigData(url, headers);
    } catch (error) {
      this.handleInitFailure(error);
    }
  }

  /**
   * Get App Config Data.
   *
   * It fetch the unified config data and returns to requested.
   *
   * @returns it returns the promised response.
   */
  public async getAppConfigData(url: string, headers = {}): Promise<void> {
    try {
      this.appConfigData = await firstValueFrom(this.http.get<AppInitConfig>(url, { headers }));

      // temporary? fix to remove null group ids or product ids for OLA lender configuration #265888
      this.removeNullLoansProductsAndMemberships(this.appConfigData.admin);

      // This is a temporary solution, will be updated/removed
      if (this.appConfigData?.features) {
        const saveAppDataToSessionStorageFF = this.appConfigData?.features?.filter((item) => item.name === 'SaveAppDataToSessionStorage');

        sessionStorage.setItem(
          'SaveAppDataToSessionStorage',
          saveAppDataToSessionStorageFF.length > 0 ? saveAppDataToSessionStorageFF[0].isEnabled.toString() : false.toString()
        );
      }
    } catch (error) {
      this.appConfigData = null;
      this.handleInitFailure(error);
    }
  }

  /**
   * Extracts a specific configuration object from a larger configuration object based on a provided key.
   *
   * The method identifies the type of configuration requested ('features', 'lender', 'workflow', 'admin', 'picklist') and extracts the
   * corresponding configuration data from the provided larger config object. It does this by delegating to a `filterConfigByKeys` method.
   *
   * @param data - The larger configuration object from which to extract a specific configuration.
   * @param key - The key identifying the type of config to extract. Expected to be one of
   * 'features', 'lender', 'workflow', 'admin', 'picklist'.
   *
   * @returns A specific configuration object corresponding to the provided key. The structure of this object varies based on
   * the provided key. If the key does not match any of the expected types, it will return `undefined`.
   *
   * @throws {Error} Will throw an error if the `filterConfigByKeys` method encounters a problem while filtering the configuration.
   */
  public getConfigByKey<T>(key: keyof AppInitConfig): T {
    if (this.appConfigData && typeof this.appConfigData === 'object' && key in this.appConfigData) {
      return this.appConfigData[key] as T;
    }
    return null;
  }

  /**
   * Handles failures during application initialization by logging the error and navigating to a specified error route.
   *
   * This method is intended to be used as a centralized error handler during the app configuration initialization process.
   * It logs the encountered error to the console and redirects the user to a predefined error page, helping to maintain
   * a smooth user experience by providing feedback on initialization failures.
   *
   * @param error - The error object encountered during the initialization process. This can be any type of error.
   * @param redirectRoute - Optional. The route to which the user will be redirected in case of an error.
   * Defaults to 'oops' if not specified. This route should be configured in the application's routing module.
   */
  public handleInitFailure(error, redirectRoute: string = 'oops'): void {
    // This causes extra errors in the console, so it's commented out for now
    // This likely means appInit failed for some reason, so the error will show up as a
    // network request error, no need to add 6 more console errors on top of that.
    console.warn('Initialization error:', error);
    this.router.navigate([redirectRoute]);
  }

  /**
   * Filters out invalid loan groups and loan products, as well as invalid memberships.
   * @param config The admin config to filter on.
   */
  private removeNullLoansProductsAndMemberships(config: AdminConfig): void {
    // loan groups and loan products
    if (config.olaConfiguration && config.olaConfiguration.consumerLoanGroups) {
      let consumerLoanGroups: ConsumerLoanGroup[] = config.olaConfiguration.consumerLoanGroups;
      consumerLoanGroups = consumerLoanGroups.filter((g) => g.groupId && g.displayName && g.loanGroup);
      for (const group of consumerLoanGroups) {
        group.loanProducts = group.loanProducts.filter((p) => p.productId && p.displayName);
      }
      config.olaConfiguration.consumerLoanGroups = consumerLoanGroups;
    }
    // memberships
    if (config?.oaoConfiguration?.memberships) {
      config.oaoConfiguration.memberships = config.oaoConfiguration.memberships.filter((m) => m.membershipID && m.membershipName);
    }
  }
}
